import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { ClientFlowDialog } from "@/components/clients/ClientFlowDialog";
import { PlanApprovedWorkDialog } from "@/components/schedule/PlanApprovedWorkDialog";
import { KpiCard } from "@/components/shared/KpiCard";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import { StateCard } from "@/components/shared/StateCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useToast } from "@/hooks/use-toast";
import { type DashboardRange } from "@/lib/dashboard";
import type { ProjectItem, ProjectPaymentMethod } from "@/lib/projects-clients";
import {
  Clock,
  DollarSign,
  XCircle,
  FileText,
  Plus,
  AlertTriangle,
  Pen,
  Inbox,
  CheckSquare,
  Building2,
  Users,
  MessageSquareWarning,
  Wallet,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import { useClients, useCompanyProfileQuery, useCompanySettingsQuery, useDashboard, useProjects, useScheduleBlocks } from "@/hooks/use-app-queries";

type Severity = "critical" | "warning" | "info";
const severityStyles: Record<Severity, string> = {
  critical: "text-destructive bg-destructive/5 border-destructive/20",
  warning: "text-warning bg-warning/5 border-warning/20",
  info: "text-info bg-info/5 border-info/20",
};

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Skeleton className="h-72 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    </div>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function isApprovedNotScheduledProject(project: {
  archivedAt?: string | null;
  status?: string;
  approvedTicketCount?: number;
  latestApprovedTicketAt?: string | null;
  hasScheduleAfterLatestApproval?: boolean;
}) {
  const approvedTicketCount = Number(project.approvedTicketCount ?? 0);
  return (
    !project.archivedAt &&
    ["ACTIVE", "ON_HOLD"].includes(project.status ?? "ACTIVE") &&
    approvedTicketCount > 0 &&
    Boolean(project.latestApprovedTicketAt) &&
    !project.hasScheduleAfterLatestApproval
  );
}

function formatRelativeTime(value: string, t: (key: string, options?: Record<string, unknown>) => string) {
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 1) return t("dashboard.relativeTime.justNow");
  if (minutes < 60) return t("dashboard.relativeTime.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("dashboard.relativeTime.hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("dashboard.relativeTime.daysAgo", { count: days });
}

function mapActivityLabel(eventType: string, t: (key: string) => string) {
  switch (eventType) {
    case "TICKET_CREATED":
      return t("dashboard.activityLabels.created");
    case "DRAFT_CREATED":
      return t("dashboard.activityLabels.created");
    case "TICKET_UPDATED":
      return t("dashboard.activityLabels.updated");
    case "SENT_FOR_APPROVAL":
      return t("dashboard.activityLabels.sentForApproval");
    case "APPROVAL_LINK_OPENED":
      return t("dashboard.activityLabels.openedByRecipient");
    case "REMINDER_SENT":
      return t("dashboard.activityLabels.reminderSent");
    case "APPROVAL_SMS_SENT":
      return t("dashboard.activityLabels.approvalSmsSent");
    case "APPROVAL_LINK_SMS_RESENT":
      return t("dashboard.activityLabels.approvalLinkSmsResent");
    case "REMINDER_SMS_SENT":
      return t("dashboard.activityLabels.reminderSmsSent");
    case "SMS_STATUS_QUEUED":
      return t("dashboard.activityLabels.smsQueued");
    case "SMS_STATUS_SENT":
      return t("dashboard.activityLabels.smsSent");
    case "SMS_STATUS_DELIVERED":
      return t("dashboard.activityLabels.smsDelivered");
    case "SMS_STATUS_FAILED":
      return t("dashboard.activityLabels.smsFailed");
    case "SMS_STATUS_UNDELIVERED":
      return t("dashboard.activityLabels.smsUndelivered");
    case "APPROVED":
      return t("dashboard.activityLabels.approved");
    case "REJECTED":
      return t("dashboard.activityLabels.rejected");
    case "CLARIFICATION_REQUESTED":
      return t("dashboard.activityLabels.clarificationRequested");
    case "CLARIFICATION_RESPONSE_SENT":
      return t("dashboard.activityLabels.clarificationResponseSent");
    case "COMMENT_ADDED":
      return t("dashboard.activityLabels.commentAdded");
    case "DELETED":
      return t("dashboard.activityLabels.deleted");
    case "PAYMENT_RECORDED":
      return t("dashboard.activityLabels.paymentReceived");
    case "SCHEDULE_BLOCK_SAVED":
      return t("dashboard.activityLabels.scheduleSaved");
    case "SCHEDULE_MESSAGE_SENT":
      return t("dashboard.activityLabels.scheduleMessageSent");
    default:
      return eventType.toLowerCase().replace(/_/g, " ");
  }
}

function formatPaymentMethodLabel(method: ProjectPaymentMethod, t: (key: string) => string) {
  switch (method) {
    case "CASH":
      return t("paymentMethods.cash");
    case "CHECK":
      return t("paymentMethods.check");
    case "CARD":
      return t("paymentMethods.card");
    case "ZELLE":
      return t("paymentMethods.zelle");
    case "ACH":
      return t("paymentMethods.ach");
    case "OTHER":
    default:
      return t("paymentMethods.other");
  }
}

function buildBaseFilters(filters: { range: DashboardRange; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  query.set("range", filters.range);
  if (filters.startDate) query.set("startDate", filters.startDate);
  if (filters.endDate) query.set("endDate", filters.endDate);
  return query;
}

function buildProjectScopedQuery(projectId?: string | null) {
  const query = new URLSearchParams();
  if (projectId) query.set("projectId", projectId);
  return query;
}

function withTrendDisplayOffsets(
  rows: Array<{ label: string; ticketsAdded: number; approved: number; rejected: number }>,
) {
  return rows.map((row) => {
    const overlapsApproved = row.approved === row.ticketsAdded || row.approved === row.rejected;
    const overlapsRejected = row.rejected === row.ticketsAdded || row.rejected === row.approved;
    const overlapsAdded = row.ticketsAdded === row.approved || row.ticketsAdded === row.rejected;

    return {
      ...row,
      ticketsAddedDisplay: row.ticketsAdded,
      approvedDisplay: row.approved + (overlapsApproved ? 0.08 : 0),
      rejectedDisplay: row.rejected + (overlapsRejected ? 0.16 : 0),
    };
  });
}

function toLocalDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function timeStringToMinutes(value?: string | null, fallback = 0) {
  if (!value) return fallback;
  const [hours, minutes] = value.split(":").map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return hours * 60 + minutes;
}

function formatHourTick(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const suffix = hours >= 12 ? "PM" : "AM";
  const normalized = hours % 12 || 12;
  return `${normalized} ${suffix}`;
}

function formatTimelineBlockRange(startAt: string, endAt: string) {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const selectedRange: DashboardRange = "this_month";
  const [trendRange, setTrendRange] = useState<Extract<DashboardRange, "this_week" | "this_month" | "last_six_months">>("this_month");
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [hoveredTimelineBlockId, setHoveredTimelineBlockId] = useState<string | null>(null);
  const [pinnedTimelineBlockId, setPinnedTimelineBlockId] = useState<string | null>(null);

  const [setupLoading, setSetupLoading] = useState(true);
  const [openAddClientModal, setOpenAddClientModal] = useState(false);
  const [planningProject, setPlanningProject] = useState<ProjectItem | null>(null);
  const [dismissedAddClientModal, setDismissedAddClientModal] = useState(false);
  const dashboardQuery = useDashboard(selectedRange);
  const trendQuery = useDashboard(trendRange);
  const dashboard = dashboardQuery.data ?? null;
  const trendData = trendQuery.data?.ticketFlowTrend ?? [];
  const trendChartData = useMemo(() => withTrendDisplayOffsets(trendData), [trendData]);
  const loading = dashboardQuery.isLoading && !dashboardQuery.data;
  const error = dashboardQuery.error instanceof Error ? dashboardQuery.error.message : null;
  const isRefreshing =
    (dashboardQuery.isFetching && Boolean(dashboardQuery.data)) ||
    (trendQuery.isFetching && Boolean(trendQuery.data));
  const settingsQuery = useCompanySettingsQuery();
  const companyProfileQuery = useCompanyProfileQuery();
  const today = useMemo(() => toLocalDateInput(new Date()), []);
  const todayScheduleQuery = useScheduleBlocks(today);
  const setupProjectsQuery = useProjects();
  const setupClientsQuery = useClients();
  const workdayStartMinutes = useMemo(
    () => timeStringToMinutes(settingsQuery.data?.scheduleWorkdayStartTime, 8 * 60),
    [settingsQuery.data?.scheduleWorkdayStartTime],
  );
  const workdayEndMinutes = useMemo(() => {
    const parsed = timeStringToMinutes(settingsQuery.data?.scheduleWorkdayEndTime, 18 * 60);
    return parsed > workdayStartMinutes ? parsed : workdayStartMinutes + 60;
  }, [settingsQuery.data?.scheduleWorkdayEndTime, workdayStartMinutes]);
  const todayTimelineBlocks = useMemo(
    () =>
      (todayScheduleQuery.data?.items ?? [])
        .filter((item) => item.status !== "CANCELED")
        .map((item) => {
          const start = new Date(item.startAt);
          const end = new Date(item.endAt);
          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const endMinutes = end.getHours() * 60 + end.getMinutes();
          return {
            id: item.id,
            projectName: item.project.name,
            clientName: item.project.client?.companyName || item.project.client?.name || "",
            address: item.project.jobSiteAddress || "",
            startMinutes,
            endMinutes,
            startAt: item.startAt,
            endAt: item.endAt,
            status: item.status,
            assignedMemberName: item.assignedMember?.fullName || item.assignedMember?.email || "",
          };
        })
        .sort((left, right) => left.startMinutes - right.startMinutes),
    [todayScheduleQuery.data?.items],
  );
  const timelineSpanMinutes = Math.max(60, workdayEndMinutes - workdayStartMinutes);
  const timelineTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let minute = workdayStartMinutes; minute <= workdayEndMinutes; minute += 60) {
      ticks.push(minute);
    }
    if (ticks[ticks.length - 1] !== workdayEndMinutes) ticks.push(workdayEndMinutes);
    return ticks;
  }, [workdayEndMinutes, workdayStartMinutes]);
  const currentTimelineMinutes = useMemo(() => {
    const now = new Date(currentTime);
    return now.getHours() * 60 + now.getMinutes();
  }, [currentTime]);
  const starterTicketLimitReached =
    user?.currentPlan === "STARTER" &&
    companyProfileQuery.data?.planEntitlements.tickets.limitReached === true;
  const starterTicketUsage = companyProfileQuery.data?.planEntitlements.tickets.used ?? 0;
  const starterTicketMax = companyProfileQuery.data?.planEntitlements.tickets.max ?? 5;
  const currentTimelineLeft = useMemo(() => {
    if (currentTimelineMinutes <= workdayStartMinutes) return 0;
    if (currentTimelineMinutes >= workdayEndMinutes) return 100;
    return ((currentTimelineMinutes - workdayStartMinutes) / timelineSpanMinutes) * 100;
  }, [currentTimelineMinutes, timelineSpanMinutes, workdayEndMinutes, workdayStartMinutes]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 15 * 60 * 1000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (
      (setupProjectsQuery.isLoading && !setupProjectsQuery.data) ||
      (setupClientsQuery.isLoading && !setupClientsQuery.data)
    ) {
      setSetupLoading(true);
      return;
    }

    const clientRows = setupClientsQuery.data ?? [];
    if (clientRows.length === 0) {
      if (!dismissedAddClientModal) {
        setOpenAddClientModal(true);
      }
    } else if (dismissedAddClientModal) {
      setDismissedAddClientModal(false);
    }

    setSetupLoading(false);
  }, [
    dismissedAddClientModal,
    openAddClientModal,
    setupProjectsQuery.isLoading,
    setupProjectsQuery.data,
    setupClientsQuery.isLoading,
    setupClientsQuery.data,
  ]);

  const baseFilterQuery = useMemo(() => {
    const query = buildBaseFilters({
      range: selectedRange,
      startDate: dashboard?.filters.startDate,
      endDate: dashboard?.filters.endDate,
    });
    return query;
  }, [selectedRange, dashboard?.filters.startDate, dashboard?.filters.endDate]);
  const projectScopedQuery = useMemo(
    () => buildProjectScopedQuery(dashboard?.filters.projectId),
    [dashboard?.filters.projectId],
  );

  const checklistItems = useMemo(() => {
    if (!dashboard) return [];
    const items = [
      { label: t("dashboard.checklist.uploadLogo"), href: "/settings", done: dashboard.checklist.hasLogo, icon: Building2 },
      { label: t("dashboard.checklist.createFirstClient"), href: "/clients", done: dashboard.checklist.hasClient, icon: CheckSquare },
      { label: t("dashboard.checklist.createFirstProject"), href: "/projects", done: dashboard.checklist.hasProject, icon: CheckSquare },
      { label: t("dashboard.checklist.createFirstTicket"), href: "/tickets/new", done: dashboard.checklist.hasTicket, icon: CheckSquare },
    ];

    if (user?.currentPlan === "TEAM") {
      items.splice(1, 0, {
        label: t("dashboard.checklist.inviteTeam"),
        href: "/team",
        done: dashboard.checklist.hasTeamMemberBeyondOwner,
        icon: Users,
      });
    }

    return items;
  }, [dashboard, t, user?.currentPlan]);

  const allChecklistDone = checklistItems.length > 0 && checklistItems.every((item) => item.done);

  const priorityCards = useMemo(() => {
    if (!dashboard) return [];

    const clarificationCount = dashboard.actionRequired.clarificationsRequested ?? 0;
    const pendingApprovalCount = Math.max(
      0,
      (dashboard.kpis.pendingTickets ?? 0) - clarificationCount,
    );
    const rejectedCount = dashboard.actionRequired.rejectedNeedingRevision ?? 0;
    const paymentFollowupValue = (setupProjectsQuery.data ?? [])
      .filter((project) => project.status === "COMPLETED" && Number(project.balance ?? 0) > 0)
      .reduce((sum, project) => sum + Number(project.balance ?? 0), 0);

    return [
      (() => {
        const query = new URLSearchParams(projectScopedQuery);
        query.set("status", "PENDING_APPROVAL");
        return {
          key: "pending",
          title: t("dashboard.pendingApproval"),
          count: pendingApprovalCount,
          emptyText: t("dashboard.allCaughtUp"),
          icon: Inbox,
          className: "border-amber-500/45 bg-transparent hover:bg-amber-500/5",
          iconClassName: "text-amber-600",
          textClassName: "text-amber-700",
          subtitleClassName: "text-amber-700/80",
          onClick: () => {
            navigate(`/approvals?${query.toString()}`);
          },
        };
      })(),
      {
        key: "clarification",
        title: t("dashboard.needClarification"),
        count: clarificationCount,
        emptyText: t("dashboard.allCaughtUp"),
        icon: MessageSquareWarning,
        className: "border-sky-500/45 bg-transparent hover:bg-sky-500/5",
        iconClassName: "text-sky-600",
        textClassName: "text-sky-700",
        subtitleClassName: "text-sky-700/80",
        onClick: () => {
          const query = new URLSearchParams(projectScopedQuery);
          query.set("status", "NEEDS_CLARIFICATION");
          if (clarificationCount === 1) {
            const item = dashboard.recentActivity.find(
              (activity) => activity.ticket?.status === "NEEDS_CLARIFICATION",
            );
            if (item) {
              navigate(`/tickets/${item.ticket.id}`);
              return;
            }
          }
          navigate(`/tickets?${query.toString()}`);
        },
      },
      {
        key: "revision",
        title: t("dashboard.needRevision"),
        count: rejectedCount,
        emptyText: t("dashboard.allCaughtUp"),
        icon: XCircle,
        className: "border-rose-500/45 bg-transparent hover:bg-rose-500/5",
        iconClassName: "text-rose-600",
        textClassName: "text-rose-700",
        subtitleClassName: "text-rose-700/80",
        onClick: () => {
          const query = new URLSearchParams(projectScopedQuery);
          query.set("status", "REJECTED");
          if (rejectedCount === 1) {
            const item = dashboard.recentActivity.find(
              (activity) => activity.ticket?.status === "REJECTED",
            );
            if (item) {
              navigate(`/tickets/${item.ticket.id}`);
              return;
            }
          }
          navigate(`/tickets?${query.toString()}`);
        },
      },
      {
        key: "payment-followup",
        title: t("dashboard.paymentFollowUp"),
        count: paymentFollowupValue,
        emptyText: t("dashboard.allCaughtUp"),
        icon: Wallet,
        className: "border-emerald-500/45 bg-transparent hover:bg-emerald-500/5",
        iconClassName: "text-emerald-600",
        textClassName: "text-emerald-700",
        subtitleClassName: "text-emerald-700/80",
        formatter: (value: number) => (value > 0 ? formatCurrency(value) : t("dashboard.allCaughtUp")),
        onClick: () => {
          navigate("/projects?status=COMPLETED&paymentFollowup=1");
        },
      },
    ];
  }, [dashboard, navigate, projectScopedQuery, setupProjectsQuery.data, t]);

  const approvedNotScheduledProjects = useMemo(
    () =>
      [...(setupProjectsQuery.data ?? [])]
        .filter((project) => isApprovedNotScheduledProject(project))
        .sort((left, right) => {
          const leftTime = left.latestApprovedTicketAt
            ? new Date(left.latestApprovedTicketAt).getTime()
            : 0;
          const rightTime = right.latestApprovedTicketAt
            ? new Date(right.latestApprovedTicketAt).getTime()
            : 0;
          return rightTime - leftTime;
        }),
    [setupProjectsQuery.data],
  );

  const renderDashboardContent = () => {
    if (!dashboard) return null;

    const pendingQ = new URLSearchParams(projectScopedQuery);
    pendingQ.set("statuses", "PENDING_APPROVAL,NEEDS_CLARIFICATION");
    const currentStatusQuery = new URLSearchParams(projectScopedQuery);
    currentStatusQuery.set("status", "NEEDS_CLARIFICATION");
    const draftStatusQuery = new URLSearchParams(projectScopedQuery);
    draftStatusQuery.set("status", "DRAFT");

    const fieldKpis = [
      { title: t("dashboard.fieldKpis.myDrafts"), value: dashboard.actionRequired.draftsNotSent, icon: Pen, href: `/tickets?${draftStatusQuery.toString()}` },
      { title: t("dashboard.fieldKpis.pendingByMe"), value: dashboard.kpis.pendingTickets, icon: Clock, href: `/approvals?${pendingQ.toString()}` },
      { title: t("dashboard.fieldKpis.clarifications"), value: dashboard.actionRequired.clarificationsRequested, icon: AlertTriangle, href: `/tickets?${currentStatusQuery.toString()}` },
    ];

    const getActivityHref = (item: (typeof dashboard.recentActivity)[number]) => {
      if (item.ticket?.id) return `/tickets/${item.ticket.id}`;
      if (item.project?.id) return `/projects/${item.project.id}`;
      return null;
    };

    const renderActivityTitle = (item: (typeof dashboard.recentActivity)[number]) => {
      if (item.eventType === "PAYMENT_RECORDED" && item.payment) {
        return (
          <>
            <span className="font-semibold text-accent">{mapActivityLabel(item.eventType, t)}</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="font-medium text-foreground">
              {t("dashboard.paymentReceivedFrom", {
                amount: formatCurrency(item.payment.amount),
                name: item.payment.clientName || t("dashboard.unknownClient"),
              })}
            </span>
          </>
        );
      }

      if ((item.eventType === "SCHEDULE_BLOCK_SAVED" || item.eventType === "SCHEDULE_MESSAGE_SENT") && item.project) {
        return (
          <>
            <span className="font-semibold text-accent">{mapActivityLabel(item.eventType, t)}</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="font-medium text-foreground hover:underline">{item.project.name}</span>
          </>
        );
      }

      if (!item.ticket) return null;

      return (
        <>
          <span className="font-semibold text-accent">{mapActivityLabel(item.eventType, t)}</span>
          <span className="mx-1.5 text-muted-foreground">·</span>
          <span className="font-medium text-foreground hover:underline">{item.ticket.ticketNumber}</span>
        </>
      );
    };

    const renderActivityMeta = (item: (typeof dashboard.recentActivity)[number]) => {
      if (item.eventType === "PAYMENT_RECORDED" && item.payment) {
        return (
          <div className="flex items-center gap-2 min-w-0">
            <span className="max-w-[35%] truncate text-xs text-muted-foreground">{item.actorName || t("dashboard.system")}</span>
            <span className="shrink-0 text-xs text-muted-foreground">·</span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatPaymentMethodLabel(item.payment.method, t)}</span>
            <span className="shrink-0 text-xs text-muted-foreground">·</span>
            <span className="flex-1 truncate text-xs text-muted-foreground">{item.project?.name || "-"}</span>
          </div>
        );
      }

      if ((item.eventType === "SCHEDULE_BLOCK_SAVED" || item.eventType === "SCHEDULE_MESSAGE_SENT") && item.schedule) {
        const range =
          item.schedule.startAt && item.schedule.endAt
            ? formatTimelineBlockRange(item.schedule.startAt, item.schedule.endAt)
            : null;

        return (
          <div className="flex items-center gap-2 min-w-0">
            <span className="max-w-[35%] truncate text-xs text-muted-foreground">{item.actorName || t("dashboard.system")}</span>
            {item.schedule.clientName ? (
              <>
                <span className="shrink-0 text-xs text-muted-foreground">·</span>
                <span className="max-w-[30%] truncate text-xs text-muted-foreground">{item.schedule.clientName}</span>
              </>
            ) : null}
            {range ? (
              <>
                <span className="shrink-0 text-xs text-muted-foreground">·</span>
                <span className="flex-1 truncate text-xs text-muted-foreground">{range}</span>
              </>
            ) : null}
          </div>
        );
      }

      return (
        <div className="flex items-center gap-2 min-w-0">
          <span className="max-w-[45%] truncate text-xs text-muted-foreground">{item.actorName || t("dashboard.system")}</span>
          <span className="shrink-0 text-xs text-muted-foreground">·</span>
          <span className="flex-1 truncate text-xs text-muted-foreground">{item.ticket?.project?.name || "-"}</span>
        </div>
      );
    };

    return (
      <>
        {user?.role === "FIELD" ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {fieldKpis.map((kpi) => (
              <KpiCard key={kpi.title} title={kpi.title} value={kpi.value} icon={kpi.icon} href={kpi.href} />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {priorityCards.map((card) => (
                <button
                  key={card.key}
                  type="button"
                  onClick={card.onClick}
                  className={`group w-full rounded-xl border px-4 py-4 text-left shadow-sm transition-[background-color,box-shadow,transform,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-18px_rgba(15,23,42,0.28)] focus-visible:-translate-y-0.5 focus-visible:shadow-[0_14px_30px_-18px_rgba(15,23,42,0.28)] ${card.className}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${card.subtitleClassName ?? "text-foreground/80"}`}>
                        {card.title}
                      </div>
                      <div className={`mt-2 text-2xl font-semibold leading-tight ${card.textClassName ?? "text-foreground"}`}>
                        {"formatter" in card && typeof card.formatter === "function"
                          ? card.formatter(card.count)
                          : card.count > 0
                            ? card.count
                            : card.emptyText}
                      </div>
                    </div>
                    <div className="rounded-full bg-transparent p-3">
                      <card.icon className={`h-6.5 w-6.5 ${card.iconClassName}`} />
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="px-1 pt-3 pb-1">
              <div>
                <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.todayTimeline.title", "Today's schedule")}</h3>
              </div>

              <div className="mt-3">
                {todayScheduleQuery.isLoading && !todayScheduleQuery.data ? (
                  <Skeleton className="h-28 w-full rounded-xl" />
                ) : todayTimelineBlocks.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-8 text-sm text-muted-foreground">
                    {t("dashboard.todayTimeline.empty", "No project blocks scheduled for today.")}
                  </div>
                ) : (
                  <div className="rounded-xl border border-border/70 bg-[#FCFCFD] px-4 pt-5 pb-3">
                    <div className="relative h-[82px]">
                      <div
                        className="absolute -top-3 z-20"
                        style={{
                          left: `${currentTimelineLeft}%`,
                          transform: "translateX(-50%)",
                        }}
                      >
                        <div className="h-0 w-0 border-l-[7px] border-r-[7px] border-t-[12px] border-l-transparent border-r-transparent border-t-red-500 drop-shadow-sm" />
                      </div>

                      {timelineTicks.map((tick) => {
                        const left = ((tick - workdayStartMinutes) / timelineSpanMinutes) * 100;
                        return (
                          <div
                            key={tick}
                            className="absolute inset-y-0"
                            style={{ left: `${left}%` }}
                          >
                            <div className="h-[46px] w-px bg-border/80" />
                            <span
                              className="absolute top-[52px] text-[11px] font-medium text-muted-foreground"
                              style={{
                                transform:
                                  tick === workdayStartMinutes
                                    ? "translateX(0)"
                                    : tick === timelineTicks[timelineTicks.length - 1]
                                      ? "translateX(-100%)"
                                      : "translateX(-50%)",
                              }}
                            >
                              {formatHourTick(tick)}
                            </span>
                          </div>
                        );
                      })}

                      <div className="absolute left-0 right-0 top-2 h-9 bg-muted/60" />

                      {todayTimelineBlocks.map((block) => {
                        const left = ((block.startMinutes - workdayStartMinutes) / timelineSpanMinutes) * 100;
                        const width = (Math.max(15, block.endMinutes - block.startMinutes) / timelineSpanMinutes) * 100;
                        const isTimelineBlockActive =
                          pinnedTimelineBlockId === block.id || hoveredTimelineBlockId === block.id;
                        return (
                          <Popover
                            key={block.id}
                            open={isTimelineBlockActive}
                            onOpenChange={(open) => {
                              if (!open && pinnedTimelineBlockId === block.id) {
                                setPinnedTimelineBlockId(null);
                              }
                            }}
                          >
                            <PopoverTrigger asChild>
                              <div
                                className={`absolute top-1.5 z-10 flex h-8 items-center justify-center overflow-hidden rounded-md bg-accent px-2 text-[#0C1F4F] transition-all duration-200 ease-out ${
                                  isTimelineBlockActive
                                    ? "scale-[1.08] shadow-[0_16px_30px_-12px_rgba(245,158,11,0.55)] ring-1 ring-white/70"
                                    : "shadow-[0_4px_10px_rgba(15,23,42,0.14)] hover:scale-[1.04] hover:shadow-[0_12px_24px_-12px_rgba(245,158,11,0.45)]"
                                }`}
                                style={{
                                  left: `${Math.max(0, left)}%`,
                                  width: `${Math.min(100 - Math.max(0, left), Math.max(width, 8))}%`,
                                }}
                                onMouseEnter={() => setHoveredTimelineBlockId(block.id)}
                                onMouseLeave={() =>
                                  setHoveredTimelineBlockId((current) => (current === block.id ? null : current))
                                }
                                onClick={() =>
                                  setPinnedTimelineBlockId((current) => (current === block.id ? null : block.id))
                                }
                              >
                                <div className="truncate text-[11px] font-semibold leading-none text-[#0C1F4F]">
                                  {formatTimelineBlockRange(block.startAt, block.endAt)}
                                </div>
                              </div>
                            </PopoverTrigger>
                            <PopoverContent
                              side="top"
                              align="center"
                              sideOffset={10}
                              className="max-w-[260px] rounded-xl border border-accent/80 bg-white px-4 py-3 text-[#0C1F4F] shadow-[0_24px_50px_-20px_rgba(15,23,42,0.45),0_12px_24px_-18px_rgba(245,158,11,0.35)]"
                              onMouseEnter={() => setHoveredTimelineBlockId(block.id)}
                              onMouseLeave={() =>
                                setHoveredTimelineBlockId((current) => (current === block.id ? null : current))
                              }
                            >
                              <div className="space-y-2">
                                <div className="text-sm font-semibold leading-tight">{block.projectName}</div>
                                {block.address ? (
                                  <div className="text-xs leading-relaxed text-slate-600">{block.address}</div>
                                ) : null}
                                <div className="text-xs font-medium text-slate-700">
                                  {formatTimelineBlockRange(block.startAt, block.endAt)}
                                </div>
                                {user?.currentPlan === "TEAM" && block.assignedMemberName ? (
                                  <div className="text-xs text-slate-600">{block.assignedMemberName}</div>
                                ) : null}
                              </div>
                              <PopoverArrow width={18} height={10} className="fill-[hsl(var(--accent))]" />
                            </PopoverContent>
                          </Popover>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-lg border bg-card min-h-[260px] flex flex-col">
                <div className="flex items-center justify-between gap-3 px-5 pt-5">
                  <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.ticketFlowTrend")}</h3>
                  <select
                    className="h-9 rounded-md border bg-card px-3 text-sm text-foreground"
                    value={trendRange}
                    onChange={(event) =>
                      setTrendRange(event.target.value as Extract<DashboardRange, "this_week" | "this_month" | "last_six_months">)
                    }
                  >
                    <option value="this_week">{t("dashboard.ranges.thisWeek")}</option>
                    <option value="this_month">{t("dashboard.ranges.thisMonth")}</option>
                    <option value="last_six_months">{t("dashboard.ranges.lastSixMonths")}</option>
                  </select>
                </div>
                <div className="mt-3 flex-1 px-5 pb-5 [&_.recharts-surface]:ml-[-20px]">
                  {trendData.length === 0 ? (
                    <div className="h-full rounded-md border border-dashed border-border/70 bg-muted/20 flex items-center justify-center">
                      <span className="text-sm text-muted-foreground">{t("dashboard.noTrendData")}</span>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={210}>
                      <LineChart data={trendChartData} margin={{ left: 4, right: 8, top: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <RechartsTooltip
                          formatter={(value, name, item) => {
                            const payload = item.payload as {
                              ticketsAdded: number;
                              approved: number;
                              rejected: number;
                            };
                            if (name === t("dashboard.chart.ticketsAdded")) return [payload.ticketsAdded, name];
                            if (name === t("dashboard.chart.rejected")) return [payload.rejected, name];
                            if (name === t("dashboard.chart.approved")) return [payload.approved, name];
                            return [value, name];
                          }}
                          contentStyle={{
                            background: "hsl(0, 0%, 100%)",
                            border: "1px solid hsl(215, 20%, 88%)",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="ticketsAddedDisplay"
                          name={t("dashboard.chart.ticketsAdded")}
                          stroke="hsl(var(--warning))"
                          strokeWidth={2.25}
                          dot={false}
                          strokeOpacity={0.9}
                        />
                        <Line
                          type="monotone"
                          dataKey="rejectedDisplay"
                          name={t("dashboard.chart.rejected")}
                          stroke="hsl(var(--destructive))"
                          strokeWidth={2.25}
                          dot={false}
                          strokeOpacity={0.9}
                        />
                        <Line
                          type="monotone"
                          dataKey="approvedDisplay"
                          name={t("dashboard.chart.approved")}
                          stroke="#16a34a"
                          strokeWidth={3}
                          dot={{ r: 2.5, fill: "#16a34a", strokeWidth: 0 }}
                          activeDot={{ r: 4.5, fill: "#16a34a", strokeWidth: 0 }}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="lg:col-span-1 rounded-lg border bg-card p-5 min-h-[260px] flex flex-col">
                <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.quickActions")}</h3>
                <div className="mt-3 grid flex-1 grid-cols-2 content-center gap-2 auto-rows-fr">
                  <Button
                    size="sm"
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-accent/70 bg-accent/18 px-2 text-center text-[11px] leading-[1.1] text-accent whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent hover:bg-accent/24 hover:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] focus-visible:-translate-y-0.5 focus-visible:border-accent focus-visible:bg-accent/24 focus-visible:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] sm:text-xs"
                    onClick={() => navigate("/clients?new=1")}
                  >
                    <Users className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("dashboard.actions.newClient")}</span>
                  </Button>
                  <Button
                    size="sm"
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-accent/70 bg-accent/18 px-2 text-center text-[11px] leading-[1.1] text-accent whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent hover:bg-accent/24 hover:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] focus-visible:-translate-y-0.5 focus-visible:border-accent focus-visible:bg-accent/24 focus-visible:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] sm:text-xs"
                    onClick={() => navigate("/projects?new=1")}
                  >
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("dashboard.actions.newProject")}</span>
                  </Button>
                  <Button
                    size="sm"
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-accent/70 bg-accent/18 px-2 text-center text-[11px] leading-[1.1] text-accent whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent hover:bg-accent/24 hover:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] focus-visible:-translate-y-0.5 focus-visible:border-accent focus-visible:bg-accent/24 focus-visible:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] sm:text-xs"
                    onClick={() => navigate("/tickets/new")}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("dashboard.actions.newTicket")}</span>
                  </Button>
                  <Button
                    size="sm"
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-accent/70 bg-accent/18 px-2 text-center text-[11px] leading-[1.1] text-accent whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent hover:bg-accent/24 hover:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] focus-visible:-translate-y-0.5 focus-visible:border-accent focus-visible:bg-accent/24 focus-visible:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] sm:text-xs"
                    onClick={() => navigate("/billing?recordPayment=1")}
                  >
                    <DollarSign className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("dashboard.actions.newPayment")}</span>
                  </Button>
                  <Button
                    size="sm"
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-accent/70 bg-accent/18 px-2 text-center text-[11px] leading-[1.1] text-accent whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent hover:bg-accent/24 hover:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] focus-visible:-translate-y-0.5 focus-visible:border-accent focus-visible:bg-accent/24 focus-visible:shadow-[0_10px_24px_-14px_hsl(var(--accent)/0.9)] sm:text-xs"
                    onClick={() => navigate("/schedule")}
                  >
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("dashboard.actions.newSchedule")}</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled
                    className="h-full min-h-[72px] min-w-0 flex-col justify-center gap-1 border border-dashed px-2 text-center text-[11px] leading-[1.1] whitespace-normal [text-wrap:balance] break-words shadow-sm transition-all duration-200 ease-out disabled:opacity-100 sm:text-xs"
                  >
                    <Sparkles className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0">{t("comingSoon")}</span>
                  </Button>
                </div>
              </div>

            </div>
          </>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Aging by Status intentionally hidden for now; keep dashboard focus on actions and live activity. */}

          <div className="lg:col-span-2 rounded-lg border bg-card p-5 space-y-4">
            <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.recentActivity")}</h3>
            <div className="space-y-3">
              {dashboard.recentActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("dashboard.noRecentActivity")}</p>
              ) : dashboard.recentActivity.slice(0, 12).map((item) => {
                const href = getActivityHref(item);

                return (
                <div
                  key={item.id}
                  className={`flex items-start gap-3 group -mx-2 rounded-md border border-transparent px-2 py-1.5 transition-[background-color,box-shadow,transform,border-color] duration-200 ease-out ${href ? "cursor-pointer hover:-translate-y-0.5 hover:border-border/80 hover:bg-muted/30 hover:shadow-[0_10px_22px_-18px_rgba(15,23,42,0.28)]" : ""}`}
                  onClick={href ? () => navigate(href) : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 min-w-0">
                      <p className="min-w-0 text-sm text-foreground">{renderActivityTitle(item)}</p>
                      <div className="shrink-0 text-[11px] text-muted-foreground">
                        {formatRelativeTime(item.happenedAt, t)}
                      </div>
                    </div>
                    <div className="mt-0.5 min-w-0">
                      {renderActivityMeta(item)}
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>

          <div className="order-first lg:order-none lg:col-span-1 space-y-6">
            <div className="rounded-lg border bg-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.approvedNotScheduled")}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("dashboard.approvedNotScheduledCount", { count: approvedNotScheduledProjects.length })}
                  </p>
                </div>
                <div className="rounded-full bg-accent/10 px-3 py-1 text-sm font-semibold text-accent">
                  {approvedNotScheduledProjects.length}
                </div>
              </div>
              {approvedNotScheduledProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("dashboard.allApprovedScheduled")}</p>
              ) : (
                <div className="space-y-2">
                  {approvedNotScheduledProjects.slice(0, 3).map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{project.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {project.client?.name ?? t("projects.noClient")}
                        </p>
                        {user?.currentPlan === "TEAM" ? (
                          <p className="truncate text-xs text-muted-foreground">
                            Assigned to: {project.assignedMember?.fullName || project.assignedMember?.email || "Unassigned"}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (user?.currentPlan === "TEAM") {
                            setPlanningProject(project);
                            return;
                          }
                          navigate(`/schedule?projectId=${encodeURIComponent(project.id)}`);
                        }}
                      >
                        {user?.currentPlan === "TEAM" ? "Plan Work" : t("schedule.actions.schedule")}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!setupLoading && !allChecklistDone ? (
              <div className="rounded-lg border bg-card p-5 space-y-3">
                <h3 className="font-semibold text-[#0C1F4F]">{t("dashboard.setupChecklist")}</h3>
                <div className="space-y-2">
                  {checklistItems.map((item) => (
                    <button key={item.label} className="w-full flex items-center gap-2 text-left text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate(item.href)}>
                      <item.icon className={`h-4 w-4 ${item.done ? "text-success" : ""}`} />
                      <span>{item.label}</span>
                      {item.done ? <span className="ml-auto text-xs text-success">{t("dashboard.done")}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
        actions={
          <div className="h-4 min-w-[88px] flex items-center justify-end">
            <RefreshingIndicator visible={isRefreshing} />
          </div>
        }
      />

      {starterTicketLimitReached ? (
        <Alert className="border-amber-500/40 bg-amber-50 text-amber-950">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle>{t("dashboard.starterTicketLimitBanner.title")}</AlertTitle>
          <AlertDescription className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span>
              {t("dashboard.starterTicketLimitBanner.description", {
                used: starterTicketUsage,
                max: starterTicketMax,
              })}
            </span>
            <Button
              type="button"
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate("/upgrade-plan?targetPlan=pro&interval=MONTHLY")}
            >
              {t("dashboard.starterTicketLimitBanner.action")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <StateCard icon={AlertTriangle} tone="error" title={t("dashboard.unavailable")} description={error} primaryActionLabel={t("dashboard.retry")} onPrimaryAction={() => void dashboardQuery.refetch()} />
      ) : loading ? (
        <DashboardSkeleton />
      ) : (
        renderDashboardContent()
      )}

      <ClientFlowDialog
        open={openAddClientModal}
        onOpenChange={(open) => {
          setOpenAddClientModal(open);
          if (!open) {
            setDismissedAddClientModal(true);
          }
        }}
      />
      <PlanApprovedWorkDialog
        open={Boolean(planningProject)}
        onOpenChange={(open) => {
          if (!open) setPlanningProject(null);
        }}
        context={
          planningProject
            ? {
                projectId: planningProject.id,
                projectName: planningProject.name,
                clientName: planningProject.client?.name,
                assignedMemberId: planningProject.assignedMember?.id,
              }
            : null
        }
      />
    </div>
  );
}
